import React from "react"
import { Footer } from "./Footer"
import { Grid, Dropdown } from "semantic-ui-react"
import { FormContainer, Worksheet, LabeledInput, CheckboxYesNo } from "uikit"
import ConnectedAccountBar from "../../ConnectedAccountBar"
import HumanTimeSince from "components/HumanTimeSince"
import { useStore } from "store"

const PrivateStudentLoanAccountForm = ({
  data: {
    fields: {
      person_id,
      balance,
      interest_rate,
      monthly_payment,
      additional_payment,
      is_qualified
    },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  submitLabel = "Done",
  submitHeader = "",
  isNew,
  onDelete,
  ownerOptions,
  lastUpdatedAt,
  account
}) => {
  return (
    <Grid>
      {account.connection_id ? (
        <Grid.Row style={{ borderBottom: "1px solid #ccc" }}>
          <Grid.Column>
            <ConnectedAccountBar {...account} />
          </Grid.Column>
        </Grid.Row>
      ) : null}
      <Grid.Row>
        <Grid.Column>
          <Worksheet error={error} style={{ borderTop: "1px solid #ccc" }}>
            <Worksheet.Body>
              <Worksheet.Row
                label="Who is the Student Loan holder/owner?"
                paddedField
              >
                <Worksheet.Field error={person_id.error}>
                  <Dropdown
                    {...person_id}
                    placeholder="Account Holder"
                    options={ownerOptions}
                    selection
                  />
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="What is the current balance of your loan?"
                description={
                  account.connection_id
                    ? "Automatically updated through the account connection"
                    : null
                }
                paddedField
              >
                <Worksheet.Field error={balance.error}>
                  <LabeledInput
                    {...balance}
                    label=""
                    iconText="$"
                    iconPosition="left"
                    placeholder="0"
                    disabled={!!account.connection_id}
                    data-testid="balance"
                  />
                  {lastUpdatedAt}
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row label="What is the interest rate?" paddedField>
                <Worksheet.Field error={interest_rate.error} padded>
                  <LabeledInput
                    {...interest_rate}
                    label=""
                    iconText="%"
                    placeholder="0"
                    data-testid="interest_rate"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="What is the minimum monthly payment?"
                paddedField
              >
                <Worksheet.Field error={monthly_payment.error} padded>
                  <LabeledInput
                    {...monthly_payment}
                    label=""
                    iconText="$"
                    iconPosition="left"
                    placeholder="0"
                    data-testid="monthly_payment"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="Do you make additional payments above and beyond the required monthly payment?"
                paddedField
              >
                <Worksheet.Field>
                  <CheckboxYesNo
                    fluid
                    checked={additional_payment.value !== ""}
                    toggle={() => {
                      if (additional_payment.value !== "") {
                        additional_payment.onChange(null, { value: "" })
                      } else {
                        additional_payment.onChange(null, { value: 0 })
                      }
                    }}
                    name="additionalPaymentToggle"
                  />
                </Worksheet.Field>
                {additional_payment.value !== "" ? (
                  <Worksheet.Row
                    paddedField
                    label="How much additional payment do you make each month?"
                  >
                    <Worksheet.Field padded>
                      <LabeledInput
                        {...additional_payment}
                        label=""
                        iconText="$"
                        iconPosition="left"
                        placeholder="0"
                      />
                    </Worksheet.Field>
                  </Worksheet.Row>
                ) : null}
              </Worksheet.Row>
              <Worksheet.Row
                label="Is this a qualified student loan?"
                description={
                  <>
                    A qualified student loan is a loan that has been used for
                    the sole purpose of paying qualified educational expenses.
                    Such expenses must be funded by the loan within 90 days
                    before or after a specific academic period.
                  </>
                }
                paddedField
              >
                <Worksheet.Field error={is_qualified.error}>
                  <CheckboxYesNo {...is_qualified} fluid />
                </Worksheet.Field>
              </Worksheet.Row>
            </Worksheet.Body>
            <Footer
              isNew={isNew}
              submitLabel={submitLabel}
              onSubmit={onSubmit}
              onDelete={onDelete}
              isModified={isModified}
              deleteLabel={account.connection_id ? "Ignore Account" : "Delete"}
              hasConnectionId={account.connection_id ? true : false}
              error={error}
            />
          </Worksheet>{" "}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

const PrivateStudentLoanAccountFormContainer = FormContainer({
  person_id: {
    type: "string",
    required: true
  },
  balance: {
    type: "unit",
    required: true,
    negative: true
  },
  interest_rate: {
    type: "decimal",
    minValue: 0,
    maxValue: 100
  },
  monthly_payment: { type: "unit", required: false },
  additional_payment: { type: "unit", required: false },
  is_qualified: {
    type: "boolean",
    defaultValue: true
  }
})(PrivateStudentLoanAccountForm)

const PrivateStudentLoanAccount = ({ household, account, ...formProps }) => {
  const isNew = account === null
  let values = isNew ? {} : account
  const lastUpdatedAt = !isNew ? (
    <p
      style={{
        fontSize: "0.8em",
        textAlign: "right",
        fontWeight: "normal"
      }}
    >
      Updated <HumanTimeSince time={values.balance_updated_at} /> ago
    </p>
  ) : null
  const [peopleObject] = useStore("householdPeople", household.id)
  const people = Object.values(peopleObject)
  let ownerOptions = people
    .filter(({ role }) => role === "spouse" || role === "client")
    .map(({ id: value, first_name: text }) => ({
      value,
      text
    }))
  return (
    <PrivateStudentLoanAccountFormContainer
      {...formProps}
      values={values}
      ownerOptions={ownerOptions}
      lastUpdatedAt={lastUpdatedAt}
      account={values}
    />
  )
}

export default PrivateStudentLoanAccount
